import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ErrorNote } from "../../components/ui";
import {
  cameraAvailable, startCamera, playVideo, setTorch, stopStream,
  BARCODE_BOX, TEXT_BOX, boxStyle, cropToCanvas, copyToCanvas,
  decodeBarcodeCanvas, warmupBarcodeEngine,
  ocrVin, warmupOcr, terminateOcr,
  type VinRead,
} from "../../lib/vinScan";
import { vinDecodesClean } from "../../lib/vinDecode";

type Mode = "barcode" | "text";

export default function VinScanner({ onFound, onClose }: {
  onFound: (vin: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cropRef = useRef<HTMLCanvasElement>(document.createElement("canvas"));
  const streamRef = useRef<MediaStream | null>(null);
  const stopRef = useRef(false);
  const busyRef = useRef(false);
  const seenRef = useRef<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<Mode>("barcode");
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState("Starting the camera");
  const [torchOn, setTorchOn] = useState(false);
  const [torchable, setTorchable] = useState(false);
  const [resolution, setResolution] = useState("");
  const [hasCamera, setHasCamera] = useState(false);
  const [frames, setFrames] = useState(0);
  const [reads, setReads] = useState(0);
  const [fault, setFault] = useState<string | null>(null);
  const [diag, setDiag] = useState(false);
  const [suggested, setSuggested] =
    useState<{ raw: string; options: string[] } | null>(null);
  const [verifying, setVerifying] = useState(false);

  const box = mode === "barcode" ? BARCODE_BOX : TEXT_BOX;

  const accept = useCallback(async (read: VinRead, needsTwo: boolean) => {
    if (!read.exact) {
      stopRef.current = true;
      setSuggested({ raw: read.raw, options: read.options });
      setVerifying(true);
      const decoded = await Promise.all(read.options.map((v) => vinDecodesClean(v)));
      const real = read.options.filter((_, i) => decoded[i]);
      setVerifying(false);
      if (real.length === 1) { onFound(real[0]!); return; }
      if (real.length > 1) setSuggested({ raw: read.raw, options: real });
      return;
    }
    if (needsTwo && seenRef.current !== read.vin) {
      seenRef.current = read.vin;
      setStatus(`Read ${read.vin} — checking it again`);
      return;
    }
    stopRef.current = true;
    onFound(read.vin);
  }, [onFound]);

  useEffect(() => {
    setFrames(0); setReads(0); setFault(null); setSuggested(null);
    seenRef.current = null;
    stopRef.current = false;
  }, [mode]);

  useEffect(() => {
    let cancelled = false;
    warmupBarcodeEngine();

    const attach = async (stream: MediaStream) => {
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      await playVideo(video, stream);
      if (cancelled) return;
      setHasCamera(true);
      const track = stream.getVideoTracks()[0];
      if (track) {
        const s = track.getSettings();
        setResolution(`${s.width ?? "?"}×${s.height ?? "?"}`);
        const caps = track.getCapabilities?.() as { torch?: boolean } | undefined;
        setTorchable(!!caps?.torch);
      }
      setStatus("Hold the phone steady, close enough to fill the window");
    };

    (async () => {
      const camera = cameraAvailable();
      if (!camera.ok) {
        setErr(camera.reason ?? "No camera.");
        setStatus("You can still take a photo of the label");
        return;
      }
      try {
        const stream = await startCamera();
        if (cancelled) { stopStream(stream); return; }
        await attach(stream);
      } catch (e) {
        if (cancelled) return;
        const refused = e instanceof DOMException && e.name === "NotAllowedError";
        setErr(
          refused
            ? "Camera permission was refused. Allow it, or take a photo of the label instead."
            : "Could not start the live camera. Take a photo of the label instead.",
        );
        setStatus("Take a photo of the barcode or the stamped VIN");
      }
    })();

    return () => {
      cancelled = true;
      stopRef.current = true;
      stopStream(streamRef.current);
    };
  }, []);

  useEffect(() => {
    if (!hasCamera || suggested) return;
    stopRef.current = false;
    let timer: number;

    const tick = async () => {
      if (stopRef.current) return;
      const video = videoRef.current;
      if (!video || video.readyState < 2 || busyRef.current) {
        timer = window.setTimeout(tick, 80);
        return;
      }
      busyRef.current = true;
      try {
        const crop = cropRef.current;
        const ok = cropToCanvas(video, crop, box, {
          margin: mode === "barcode" ? 0.16 : 0.10,
          minWidth: mode === "barcode" ? 900 : 640,
          smooth: true,
        });
        if (ok) {
          setFrames((n) => n + 1);
          if (mode === "barcode") {
            const { read, payloads } = await decodeBarcodeCanvas(crop);
            if (payloads.length) {
              setReads((n) => n + 1);
              setFault(`Read: ${payloads[0]!.slice(0, 48)}`);
            }
            if (read) { await accept(read, false); return; }
          } else {
            try {
              await warmupOcr();
              const { read, text } = await ocrVin(crop);
              if (text) {
                setReads((n) => n + 1);
                setFault(`Read: ${text.replace(/\s+/g, " ").slice(0, 60)}`);
              }
              if (read) { await accept(read, true); return; }
            } catch (e) {
              setFault(`Text reader: ${(e as Error).message ?? "failed"}`);
            }
          }
        }
      } catch (e) {
        setFault((e as Error).message ?? "decode failed");
      } finally {
        busyRef.current = false;
      }
      if (!stopRef.current) timer = window.setTimeout(tick, mode === "barcode" ? 90 : 280);
    };

    timer = window.setTimeout(tick, 350);
    return () => { window.clearTimeout(timer); };
  }, [mode, hasCamera, accept, suggested, box]);

  useEffect(() => () => { terminateOcr(); }, []);

  async function handleFile(file: File) {
    setFault(null);
    setStatus("Reading the photo");
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch {
      setFault("Could not open that photo.");
      URL.revokeObjectURL(url);
      return;
    }
    const canvas = cropRef.current;
    copyToCanvas(img, canvas, 2000);
    URL.revokeObjectURL(url);
    setFrames((n) => n + 1);
    const { read, payloads } = await decodeBarcodeCanvas(canvas);
    if (payloads.length) setReads((n) => n + 1);
    if (read) { await accept(read, false); return; }
    try {
      const ocr = await ocrVin(canvas);
      if (ocr.text) {
        setReads((n) => n + 1);
        setFault(`Read: ${ocr.text.replace(/\s+/g, " ").slice(0, 60)}`);
      }
      if (ocr.read) { await accept(ocr.read, false); return; }
    } catch (e) {
      setFault(`Text reader: ${(e as Error).message ?? "failed"}`);
    }
    setFault(
      payloads[0]
        ? `Read a barcode but it was not a VIN: ${payloads[0].slice(0, 40)}`
        : "Nothing that checks out as a VIN in that photo. Fill the frame with the barcode or the stamped number and try again.",
    );
    setStatus("Try another photo — fill the frame with the VIN");
  }

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const ok = await setTorch(track, !torchOn);
    if (ok) setTorchOn(!torchOn);
  }

  return (
    <div className="scanner">
      <div className="scanner-head">
        <h3>Read the VIN</h3>
        <button className="linkbtn" onClick={onClose}>Cancel</button>
      </div>

      {suggested ? (
        verifying ? (
          <p className="hint">
            That did not read cleanly. Checking the possibilities against the
            federal database.
          </p>
        ) : (
          <>
            <p className="hint">
              This one did not read cleanly. Below is what the camera read,
              and the {suggested.options.length === 1 ? "VIN that" : "VINs that"}
              {" "}the check digit allows. Compare against the label before
              picking one — a wrong VIN here ends up on a title application.
            </p>
            <div className="diag">
              <div><span>Read as</span><span className="mono">{suggested.raw}</span></div>
            </div>
            <div className="actions" style={{ flexDirection: "column" }}>
              {suggested.options.map((vin) => (
                <Button key={vin} onClick={() => onFound(vin)}>
                  <span className="mono">{vin}</span>
                </Button>
              ))}
              <Button variant="ghost" onClick={() => {
                setSuggested(null);
                stopRef.current = false;
                setStatus("Try again — get closer and hold still");
              }}>
                No, scan again
              </Button>
            </div>
          </>
        )
      ) : (
        <>
          <div className="scanmodes">
            <button className={`scanmode ${mode === "barcode" ? "on" : ""}`}
                    onClick={() => setMode("barcode")}>
              Barcode
              <em>door jamb label</em>
            </button>
            <button className={`scanmode ${mode === "text" ? "on" : ""}`}
                    onClick={() => setMode("text")}>
              Text
              <em>windshield plate</em>
            </button>
          </div>

          <div className="scanner-frame">
            <video ref={videoRef} playsInline muted autoPlay />
            <div className="scanner-guide" style={boxStyle(box)} />
            {torchable && (
              <button className="torch" onClick={toggleTorch}
                      aria-label="Toggle the light">
                {torchOn ? "Light off" : "Light on"}
              </button>
            )}
          </div>

          {err && <ErrorNote>{err}</ErrorNote>}
          <p className="hint">{status}</p>
          {resolution && (
            <p className="hint dim">
              Camera at {resolution}. Get close — the window should be full of
              the {mode === "barcode" ? "barcode" : "VIN"} and nothing else.
            </p>
          )}

          <button className="scanbtn" onClick={() => fileRef.current?.click()}>
            Take or pick a photo
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />

          <button className="linkbtn" onClick={() => setDiag((d) => !d)}>
            {diag ? "Hide" : "Show"} what the scanner is seeing
          </button>

          {diag && (
            <div className="diag">
              <div><span>Camera</span><span>{resolution || (hasCamera ? "live" : "not started")}</span></div>
              <div><span>Frames looked at</span><span>{frames}</span></div>
              <div><span>Things read</span><span>{reads}</span></div>
              <div><span>Last message</span><span>{fault ?? "none"}</span></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
